import React, { ReactElement, useMemo } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { Box, ButtonBase, Typography } from '@mui/material';
import { trackAnalyticsEvent } from 'util/analytics';
import { useOrganizationsApi, useEventsApi } from 'services/api';
import { Routes, adminEventReviewUrl } from 'services/routes';
import { PermissionRequest } from 'services/api/apis/organizations/dto/admin-requests.dto';
import { RoleMap, useGetRole } from 'hooks/useGetRole';
import {
  COLORS,
  FONT,
  MaterialSymbol,
  SectionHeader,
  EmptyCard,
  SkeletonBlock,
  formatRelativeTime,
} from '../shared';
import { ActionRow } from './ActionRow';

export interface ActionRequiredListProps {
  schoolId?: string;

  enabled: boolean;

  showEyebrow?: boolean;
}

type ActionItem =
  | {
      kind: 'permission';
      id: string;
      eyebrow: string;
      title: string;
      subtitle: string;
      analyticsType: 'membership_request' | 'leader_access_request';
      href: string;
      createdAt: string;
    }
  | {
      kind: 'event-review';
      id: string;
      eyebrow: string;
      title: string;
      subtitle: string;
      analyticsType: 'event_needs_approval';
      href: string;
      createdAt: string;
    }
  | {
      kind: 'event-rejected';
      id: string;
      eyebrow: string;
      title: string;
      subtitle: string;
      analyticsType: 'rejected_event';
      href: string;
      createdAt: string;
    };

const MAX_ITEMS = 10;

const permissionEyebrow = (req: PermissionRequest): string => {
  if (req.requestType === 'member') return 'MEMBERSHIP REQUEST';
  if (req.requestType === 'leader') return 'LEADER ACCESS REQUEST';
  return 'ADMIN ACCESS REQUEST';
};

export const ActionRequiredList = ({
  schoolId,
  enabled,
  showEyebrow = true,
}: ActionRequiredListProps): ReactElement | null => {
  const router = useRouter();
  const role = useGetRole();
  const organizationsApi = useOrganizationsApi();
  const eventsApi = useEventsApi();

  const rPermissions = useSWR(
    enabled ? 'for-you/permission-requests' : null,
    async () => {
      const res = await organizationsApi.getPermissionRequests();
      return res.success ? res.value : { count: 0, requests: [] };
    },
  );

  const rEventReviews = useSWR(
    enabled ? ['for-you/event-reviews', schoolId] : null,
    async () => {
      const res = await eventsApi.listEventsRequests({
        search: null,
        schoolId: schoolId ?? null,
        displayFilter: 'NEEDS_APPROVAL',
        dateFilter: 'UPCOMING',
        pageSize: MAX_ITEMS,
      });
      return res.success ? res.value : { count: 0, events: [], cursor: null };
    },
  );

  const rRejectedEvents = useSWR(
    enabled ? ['for-you/rejected-events', schoolId] : null,
    async () => {
      const res = await eventsApi.listMyOrgsRejectedEvents({
        schoolId: schoolId ?? null,
        pageSize: MAX_ITEMS,
      });
      return res.success ? res.value : { count: 0, events: [], cursor: null };
    },
  );

  const items = useMemo<ActionItem[]>(() => {
    const permissionItems: ActionItem[] = (
      rPermissions.data?.requests ?? []
    ).map(req => ({
      kind: 'permission',
      id: req.id,
      eyebrow: permissionEyebrow(req),
      title: `${req.userFirstName} ${req.userLastName}`.trim(),
      subtitle: `${req.organizationName} · ${formatRelativeTime(
        req.createdAt,
      )}`,
      analyticsType:
        req.requestType === 'member'
          ? 'membership_request'
          : 'leader_access_request',
      href: Routes.admin.permissionRequests(),
      createdAt: req.createdAt,
    }));

    const reviewItems: ActionItem[] = (rEventReviews.data?.events ?? []).map(
      event => ({
        kind: 'event-review',
        id: event.id,
        eyebrow: 'EVENT NEEDS YOUR REVIEW',
        title: event.name,
        subtitle: `${
          event.hostOrganizations?.[0]?.name ?? 'Event'
        } · ${formatRelativeTime(event.createdAt)}`,
        analyticsType: 'event_needs_approval',
        href: adminEventReviewUrl(event.id, event.status),
        createdAt: event.createdAt ?? '',
      }),
    );

    const rejectedItems: ActionItem[] = (
      rRejectedEvents.data?.events ?? []
    ).map(event => ({
      kind: 'event-rejected',
      id: event.id,
      eyebrow: 'EVENT REJECTED',
      title: event.name,
      subtitle: `${
        event.hostOrganizations?.[0]?.name ?? 'Event'
      } · ${formatRelativeTime(event.createdAt)}`,
      analyticsType: 'rejected_event',
      href: adminEventReviewUrl(event.id, event.status),
      createdAt: event.createdAt ?? '',
    }));

    return [...permissionItems, ...reviewItems, ...rejectedItems]
      .sort((a, b) => {
        const aTime = new Date(a.createdAt).getTime() || 0;
        const bTime = new Date(b.createdAt).getTime() || 0;
        return bTime - aTime;
      })
      .slice(0, MAX_ITEMS);
  }, [rPermissions.data, rEventReviews.data, rRejectedEvents.data]);

  if (!enabled) return null;

  const isLoading =
    (!rPermissions.data && rPermissions.isValidating) ||
    (!rEventReviews.data && rEventReviews.isValidating) ||
    (!rRejectedEvents.data && rRejectedEvents.isValidating);

  const count = items.length;

  const handleItem = (item: ActionItem): void => {
    trackAnalyticsEvent({
      name: 'nya_item_clicked',
      props: { type: item.analyticsType },
    });
    void router.push(item.href);
  };

  const handleAdminLink = (): void => {
    trackAnalyticsEvent({ name: 'nya_admin_link_clicked' });
    // Match the nav Admin button: approvers land on event requests; others on admin default.
    const href =
      role?.includes(RoleMap.approver) ||
      role?.includes(RoleMap['sequential-approver'])
        ? Routes.admin.eventRequests()
        : Routes.admin.default();
    void router.push(href);
  };

  const countPill =
    count > 0 ? (
      <Box
        sx={{
          backgroundColor: COLORS.countBadge,
          color: COLORS.white,
          minWidth: '20px',
          height: '20px',
          px: '6px',
          borderRadius: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: '12px',
        }}
      >
        {count}
      </Box>
    ) : undefined;

  return (
    <Box width="100%">
      {showEyebrow ? (
        <SectionHeader
          eyebrow="ADMIN INBOX"
          title="Action required"
          action={countPill}
        />
      ) : (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb="12px"
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '16px',
              color: COLORS.headingDark,
              m: 0,
            }}
          >
            Action required
          </Typography>
          {countPill}
        </Box>
      )}

      <Box mt="12px">
        {isLoading ? (
          <Box>
            {[0, 1, 2].map(i => (
              <Box
                key={i}
                sx={{
                  backgroundColor: COLORS.white,
                  borderLeft: `1px solid ${COLORS.cardBorder}`,
                  borderRight: `1px solid ${COLORS.cardBorder}`,
                  borderTop: `1px solid ${COLORS.cardBorder}`,
                  borderBottom:
                    i === 2 ? `1px solid ${COLORS.cardBorder}` : 'none',
                  p: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                <SkeletonBlock width="40%" height={10} />
                <SkeletonBlock width="60%" height={14} />
                <SkeletonBlock width="50%" height={10} />
              </Box>
            ))}
          </Box>
        ) : count === 0 ? (
          <EmptyCard
            icon="check_circle"
            title="No items — nice work."
            body="Membership requests and event reviews will land here when they come in."
          />
        ) : (
          <>
            <Box>
              {items.map((item, index) => (
                <ActionRow
                  key={`${item.kind}-${item.id}`}
                  eyebrow={item.eyebrow}
                  title={item.title}
                  subtitle={item.subtitle}
                  isFirst={index === 0}
                  isLast={index === items.length - 1}
                  onClick={() => handleItem(item)}
                />
              ))}
            </Box>
            <ButtonBase
              onClick={handleAdminLink}
              disableRipple
              aria-label="Open admin portal"
              sx={{
                mt: '12px',
                pt: '4px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: COLORS.bodyDark,
                '& .AdminLink-text': {
                  fontFamily: FONT,
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: 'normal',
                  textDecoration: 'underline',
                  textDecorationColor: COLORS.bodyDark,
                  textDecorationStyle: 'solid',
                  textUnderlinePosition: 'from-font',
                },
                '& .AdminLink-icon': {
                  fontSize: '18px',
                  lineHeight: 'normal',
                },
                '&:focus-visible': {
                  outline: `2px solid ${COLORS.bodyDark}`,
                  outlineOffset: '2px',
                },
              }}
            >
              <Box component="span" className="AdminLink-text">
                Open admin portal
              </Box>
              <MaterialSymbol
                name="arrow_outward"
                size={18}
                color={COLORS.bodyDark}
                className="AdminLink-icon"
                sx={{ lineHeight: 'normal' }}
              />
            </ButtonBase>
          </>
        )}
      </Box>
    </Box>
  );
};
